import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin/questions/$quizId")({
  component: ManageQuestions,
});

interface Q {
  id: string;
  question: string;
  question_type: "multiple_choice" | "true_false";
  options: string[];
  correct_answer: string;
  explanation: string | null;
  points: number;
  sort_order: number;
}

function ManageQuestions() {
  const { quizId } = Route.useParams();
  const [quiz, setQuiz] = useState<{ title: string } | null>(null);
  const [list, setList] = useState<Q[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Q | null>(null);
  const [type, setType] = useState<"multiple_choice" | "true_false">("multiple_choice");

  const load = async () => {
    const [q, qq] = await Promise.all([
      supabase.from("quizzes").select("title").eq("id", quizId).maybeSingle(),
      supabase.from("quiz_questions").select("*").eq("quiz_id", quizId).order("sort_order"),
    ]);
    setQuiz(q.data as unknown as typeof quiz);
    setList(
      ((qq.data ?? []) as unknown as Q[]).map((r) => ({
        ...r,
        options: Array.isArray(r.options)
          ? r.options
          : typeof r.options === "string"
            ? JSON.parse(r.options as unknown as string)
            : [],
      })),
    );
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [quizId]);
  useEffect(() => {
    if (editing) setType(editing.question_type);
    else setType("multiple_choice");
  }, [editing]);

  const save = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const opts =
      type === "true_false"
        ? ["صح", "غلط"]
        : ["a", "b", "c", "d"].map((k) => String(fd.get(`opt_${k}`) ?? "")).filter(Boolean);
    const payload = {
      quiz_id: quizId,
      question: String(fd.get("question")),
      question_type: type,
      options: opts,
      correct_answer: String(fd.get("correct_answer")),
      explanation: String(fd.get("explanation") ?? "") || null,
      points: Number(fd.get("points") || 1),
      sort_order: Number(fd.get("sort_order") || 0),
    };
    const client = supabase.from("quiz_questions") as unknown as {
      update: (p: typeof payload) => {
        eq: (k: string, v: string) => Promise<{ error: { message: string } | null }>;
      };
      insert: (p: typeof payload) => Promise<{ error: { message: string } | null }>;
    };
    const { error } = editing
      ? await client.update(payload).eq("id", editing.id)
      : await client.insert(payload);
    if (error) return toast.error(error.message);
    toast.success("Saved");
    setOpen(false);
    setEditing(null);
    load();
  };

  const remove = async (id: string) => {
    if (!confirm("Delete?")) return;
    const { error } = await supabase.from("quiz_questions").delete().eq("id", id);
    if (error) return toast.error(error.message);
    load();
  };

  if (loading)
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );

  return (
    <div>
      <Link to="/admin/quizzes" className="text-sm text-muted-foreground hover:text-primary">
        ← كل الكويزات
      </Link>
      <div className="flex items-center justify-between mt-2 mb-6 flex-wrap gap-3">
        <h1 className="text-3xl font-extrabold">{quiz?.title} — Questions</h1>
        <Dialog
          open={open}
          onOpenChange={(o) => {
            setOpen(o);
            if (!o) setEditing(null);
          }}
        >
          <DialogTrigger asChild>
            <Button
              className="rounded-full bg-gradient-primary text-primary-foreground border-0"
              onClick={() => setEditing(null)}
            >
              <Plus className="h-4 w-4 me-2" /> Add Question
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing ? "Edit" : "Create"} Question</DialogTitle>
            </DialogHeader>
            <form onSubmit={save} className="space-y-3">
              <div className="space-y-1">
                <Label>السؤال</Label>
                <Textarea name="question" required defaultValue={editing?.question} rows={2} />
              </div>
              <div className="space-y-1">
                <Label>النوع</Label>
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value as typeof type)}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="multiple_choice">اختيار من متعدد</option>
                  <option value="true_false">صح / غلط</option>
                </select>
              </div>
              {type === "multiple_choice" &&
                (["a", "b", "c", "d"] as const).map((k, i) => (
                  <div key={k} className="space-y-1">
                    <Label>اختيار {String.fromCharCode(65 + i)}</Label>
                    <Input
                      name={`opt_${k}`}
                      defaultValue={editing?.options?.[i] ?? ""}
                      required={i < 2}
                    />
                  </div>
                ))}
              <div className="space-y-1">
                <Label>
                  الإجابة الصحيحة {type === "true_false" ? "(صح / غلط)" : "(انسخ نص الاختيار)"}
                </Label>
                <Input name="correct_answer" required defaultValue={editing?.correct_answer} />
              </div>
              <div className="space-y-1">
                <Label>التفسير</Label>
                <Textarea name="explanation" defaultValue={editing?.explanation ?? ""} rows={2} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>نقاط</Label>
                  <Input type="number" name="points" defaultValue={editing?.points ?? 1} />
                </div>
                <div className="space-y-1">
                  <Label>ترتيب</Label>
                  <Input type="number" name="sort_order" defaultValue={editing?.sort_order ?? 0} />
                </div>
              </div>
              <DialogFooter>
                <Button
                  type="submit"
                  className="rounded-full bg-gradient-primary text-primary-foreground border-0"
                >
                  Save
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-3">
        {list.map((q, i) => (
          <Card key={q.id} className="rounded-2xl border-0 shadow-soft">
            <CardContent className="p-4">
              <div className="flex justify-between gap-3 mb-2">
                <div className="font-bold flex-1">
                  {i + 1}. {q.question}
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={() => {
                      setEditing(q);
                      setOpen(true);
                    }}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button size="icon" variant="destructive" onClick={() => remove(q.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="text-sm text-muted-foreground space-y-1">
                {q.options.map((o, j) => (
                  <div
                    key={j}
                    className={o === q.correct_answer ? "text-emerald-600 font-bold" : ""}
                  >
                    {String.fromCharCode(65 + j)}. {o} {o === q.correct_answer && "✓"}
                  </div>
                ))}
                <div className="text-xs">
                  نقاط: {q.points} • نوع: {q.question_type}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {!list.length && <p className="text-muted-foreground text-center py-8">لا توجد أسئلة</p>}
      </div>
    </div>
  );
}
